import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import type { Store } from 'redux';
import checkVersion from './checkVersion';
import * as styles from './RootErrorBoundaryFallback.module.scss';
import type { ErrorBoundaryType } from '../components/ErrorBoundary';
import SlabLogo from '../components/SlabLogo';
import Logger from '../utils/Logger';

const MESSAGES: Record<
  ErrorBoundaryType | 'versionMismatch',
  { title: string; description: string; actions?: React.ReactNode[] }
> = {
  translation: {
    title: 'Oops! Translation tool may have errored',
    description:
      'We encountered an unresolvable error typically caused by translation tools. Please disable any and reload the page, or contact our support for additional help.',
  },
  unsupportedBrowser: {
    title: 'Oops! Unsupported browser error',
    description:
      'We encountered an unresolvable error typically caused by outdated or unsupported browsers. Please update or try a different one, or contact our support for additional help.',
  },
  unknown: {
    title: 'Oops! Something went wrong',
    description:
      'We encountered an unresolvable error and have logged it for investigation. Please go back to the home page or contact our support if the issue persists.',
  },
  versionMismatch: {
    title: 'We updated Slab while you were away!',
    description: 'Please refresh your browser to get the latest.',
    actions: [
      <button
        type="button"
        className={classNames(styles.button, styles.primary)}
        key="reload"
        onClick={() => {
          if (window.desktop?.reloadAllTabs) {
            window.desktop.reloadAllTabs();
          } else {
            document.location.reload();
          }
        }}
      >
        Get the Latest
      </button>,
    ],
  },
};

interface RootErrorBoundaryFallbackProps {
  type: ErrorBoundaryType;
  client: ApolloClient<NormalizedCacheObject>;
  store: Store;
}

const RootErrorBoundaryFallback = ({
  type,
  client,
  store,
}: RootErrorBoundaryFallbackProps) => {
  const [isVersionMismatchModalOpen, setIsVersionMismatchModalOpen] =
    React.useState(false);

  useEffect(() => {
    if (type === 'unknown' && client && store) {
      checkVersion({ client, store })
        .then((result) => {
          if (result.isVersionMismatchModalOpen) {
            setIsVersionMismatchModalOpen(true);
          }
        })
        .catch((error) => {
          Logger.error(error);
        });
    }
  }, [type, client, store]);

  const message =
    MESSAGES[isVersionMismatchModalOpen ? 'versionMismatch' : type];

  return (
    <div className={styles.container}>
      <div className={styles.background} />
      <div className={styles.content}>
        <div className={styles.logo}>
          <SlabLogo />
        </div>
        <h2>{message.title}</h2>
        <div className={styles.description}>{message.description}</div>
        <div className={styles.actions}>
          {message?.actions ?? (
            <>
              {/* We need anchors here instead of Link component, because this is not within the router context from react */}
              {/* eslint-disable-next-line react/forbid-elements */}
              <a className={styles.button} href="mailto:support@slab.com">
                Email support
              </a>
              {/* eslint-disable-next-line react/forbid-elements */}
              <a className={classNames(styles.button, styles.primary)} href="/">
                Go back to home
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RootErrorBoundaryFallback;
